import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ProductoOrdenComercio } from 'src/app/models/ProductoOrdenComercio';
import { VademecumOrdenComercio } from 'src/app/models/VademecumOrdenComercio';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { OrderComercioService } from 'src/app/services/order-comercio.service';
import { UsuarioService } from 'src/app/services/usuario.service';

@Component({
    selector: 'app-resumen-order.comercio',
    templateUrl: './resumen-order.component.html',
    styleUrls: ['./resumen-order.component.css'],
    standalone: false
})
export class ResumenOrderComercioComponent {
  private readonly _orderComercioService = inject(OrderComercioService);
  private readonly _usuarioService = inject(UsuarioService);
  private readonly _authService = inject(AuthService);
  private readonly _loadingService = inject(LoadingService);
  private readonly router = inject(Router);

  error = signal<string>('');
  loading = signal<boolean>(false);
  isFinished = signal<boolean>(false);
  
  // tooltip ayuda
  helpOpen = signal<boolean>(false);
  helpText = computed(() => 'Este es el resúmen de tu pedido. Chequeá que todo esté correcto antes de enviarlo.');

  // data
  x_nombre_fantasia_comercio = '';
  x_nombre_cliente?: string;
  x_domicilio_comercio = '';

  productosSeleccionados: ProductoOrdenComercio[] = [];
  vademecumsSeleccionados: VademecumOrdenComercio[] = [];

  x_forma_entrega = '';
  x_forma_pago = '';

  // totales
  n_total = signal<number>(0);
  n_ahorro = signal<number>(0);

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();
      } catch {
        this.router.navigate(['/sign-in']);
        return;
      }

      // Inicializo info de la orden
      const ocSvc = this._orderComercioService;
      const ord = ocSvc.ordenComercio;

      this.x_nombre_fantasia_comercio = ocSvc.comercio?.x_nombre_fantasia ?? '';
      this.x_domicilio_comercio = ocSvc.ubicacion?.x_domicilio ?? '';

      const cli = this._usuarioService.cliente();
      const nombre = `${cli?.x_nombre ?? ''} ${cli?.x_apellido ?? ''}`.trim();
      this.x_nombre_cliente = nombre.length ? nombre : undefined;

      this.productosSeleccionados = ocSvc.productosOrdenComercio ?? [];
      this.vademecumsSeleccionados = ocSvc.vademecumsOrdenComercio ?? [];

      this.x_forma_entrega = ord.b_retiro_local
        ? 'Retiro en el local'
        : `A domicilio: ${ord.x_envio_direccion ?? ''}`;

      this.x_forma_pago = ord.b_pago_efectivo ? 'Efectivo' : 'Mercadopago';

      this.refreshTotal();
    });
  }

  toggleHelp(): void {
    this.helpOpen.update(v => !v);
  }

  // --------- carrito ----------
  addQtyVademecumCart(id_vademecum: number): void {
    const item = this.vademecumsSeleccionados.find(x => x.id_vademecum === id_vademecum);
    if (!item) return;
    item.n_cantidad = (item.n_cantidad ?? 0) + 1;
    this.refreshTotal();
  }

  removeVademecumCart(id_vademecum: number): void {
    const item = this.vademecumsSeleccionados.find(x => x.id_vademecum === id_vademecum);
    if (!item) return;

    const cant = item.n_cantidad ?? 0;
    if (cant > 1) {
      item.n_cantidad = cant - 1;
    } else {
      this.vademecumsSeleccionados = this.vademecumsSeleccionados.filter(x => x.id_vademecum !== id_vademecum);
    }
    this.refreshTotal();
  }

  addQtyProductoCart(id_producto: number): void {
    const item = this.productosSeleccionados.find(x => x.id_producto === id_producto);
    if (!item) return;
    item.n_cantidad = (item.n_cantidad ?? 0) + 1;
    this.refreshTotal();
  }

  removeProductoCart(id_producto: number): void {
    const item = this.productosSeleccionados.find(x => x.id_producto === id_producto);
    if (!item) return;

    const cant = item.n_cantidad ?? 0;
    if (cant > 1) {
      item.n_cantidad = cant - 1;
    } else {
      this.productosSeleccionados = this.productosSeleccionados.filter(x => x.id_producto !== id_producto);
    }
    this.refreshTotal();
  }

  refreshTotal(): void {
    // Persisto cambios en el service
    this._orderComercioService.vademecumsOrdenComercio = this.vademecumsSeleccionados;
    this._orderComercioService.productosOrdenComercio = this.productosSeleccionados;

    const totalV = this.vademecumsSeleccionados.reduce((acc, v) => {
      const precio = Number(v.n_precio) || 0;
      const qty = Number(v.n_cantidad) || 0;
      return acc + precio * qty;
    }, 0);

    const totalP = this.productosSeleccionados.reduce((acc, p) => {
      const precio = Number(p.n_precio) || 0;
      const qty = Number(p.n_cantidad) || 0;
      return acc + precio * qty;
    }, 0);

    const ahorroV = this.vademecumsSeleccionados.reduce((acc, v) => {
      const desc = Number(v.n_porcentaje_descuento) || 0;
      if (desc <= 0) return acc;
      const precio = Number(v.n_precio) || 0;
      const qty = Number(v.n_cantidad) || 0;
      return acc + (precio * qty * desc / 100);
    }, 0);

    const ahorroP = this.productosSeleccionados.reduce((acc, p) => {
      const desc = Number(p.n_porcentaje_descuento) || 0;
      if (desc <= 0) return acc;
      const precio = Number(p.n_precio) || 0;
      const qty = Number(p.n_cantidad) || 0;
      return acc + (precio * qty * desc / 100);
    }, 0);

    this.n_total.set(totalV + totalP);
    this.n_ahorro.set(ahorroV + ahorroP);
  }

  get totalFinal(): number {
    return this.n_total() - this.n_ahorro();
  }

  async finalizaOrder(): Promise<void> {
    this.error.set('');

    if (this.vademecumsSeleccionados.length === 0 && this.productosSeleccionados.length === 0) {
      this.error.set('Debe seleccionar un producto');
      return;
    }

    this.loading.set(true);

    await this._loadingService.run(async () => {
      await this._orderComercioService.finalizeOrdenComercio();
      this.isFinished.set(true);
    }).catch((e: any) => {
      console.error(e);
      this.error.set(String(e?.message ?? e ?? 'No se pudo enviar el pedido.'));
    }).finally(() => {
      this.loading.set(false);
    });
  }
}
